import React from 'react';
import { Link } from 'react-router-dom';
import { Trash2, ShoppingCart } from 'lucide-react';
import { useWishlist } from '../context/WishlistContext';
import { useCart } from '../context/CartContext';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';

const WishlistPage = () => {
  const { wishlist, loading, removeFromWishlist } = useWishlist();
  const { addToCart } = useCart();

  const handleAddToCart = async (productId, productName) => {
    const result = await addToCart(productId, 1);
    if (result.success) {
      toast.success(`${productName} added to cart`);
    } else {
      toast.error(result.error);
    }
  };

  const handleRemove = async (productId) => {
    const result = await removeFromWishlist(productId);
    if (result.success) {
      toast.success('Removed from wishlist');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#050505] py-8">
        <div className="max-w-4xl mx-auto px-4">
          <h1 className="text-3xl font-bold text-white mb-8" style={{ fontFamily: 'Outfit, sans-serif' }}>
            My Wishlist
          </h1>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {[...Array(3)].map((_, i) => (
              <div key={i} className="glass-card p-4">
                <div className="skeleton h-48 w-full rounded-lg mb-4" />
                <div className="skeleton h-4 w-3/4 mb-2" />
                <div className="skeleton h-6 w-1/4" />
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#050505] py-8" data-testid="wishlist-page">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <h1 className="text-3xl font-bold text-white mb-8" style={{ fontFamily: 'Outfit, sans-serif' }}>
          My Wishlist
        </h1>

        {wishlist.items.length === 0 ? (
          <div className="text-center py-16" data-testid="empty-wishlist">
            <p className="text-[#A1A7B5] text-lg mb-4">Your wishlist is empty</p>
            <Link to="/products">
              <Button className="btn-primary">
                Browse Products
              </Button>
            </Link>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {wishlist.items.map((product) => (
              <div key={product.id} className="glass-card p-4" data-testid={`wishlist-item-${product.id}`}>
                <Link to={`/products/${product.id}`}>
                  <img
                    src={product.image_url || 'https://images.pexels.com/photos/5922101/pexels-photo-5922101.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940'}
                    alt={product.name}
                    className="w-full h-48 object-cover rounded-lg mb-4"
                  />
                </Link>
                <div className="mb-4">
                  <span className="text-[#00F0FF] text-xs font-medium uppercase tracking-wider">
                    {product.category}
                  </span>
                  <Link to={`/products/${product.id}`}>
                    <h3 className="text-white font-semibold text-lg hover:text-[#00F0FF] transition-colors">
                      {product.name}
                    </h3>
                  </Link>
                  <p className="text-white font-bold text-xl mt-2">
                    ${product.price.toFixed(2)}
                  </p>
                </div>
                <div className="flex space-x-2">
                  <Button
                    onClick={() => handleAddToCart(product.id, product.name)}
                    className="btn-primary flex-1 flex items-center justify-center space-x-2"
                    data-testid={`wishlist-add-to-cart-${product.id}`}
                  >
                    <ShoppingCart className="w-4 h-4" />
                    <span>Add to Cart</span>
                  </Button>
                  <Button
                    onClick={() => handleRemove(product.id)}
                    className="btn-secondary p-3"
                    data-testid={`wishlist-remove-${product.id}`}
                  >
                    <Trash2 className="w-4 h-4 text-red-400" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default WishlistPage;
